import { useNavigate } from 'react-router-dom';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import {
  ANALYTICS_SECTION_ID,
  type AnalyticsSection,
} from '@backrs_ui/grow-ui/src/pages/CoachingAnalytics/analytics.anchors';
import { HOME_LAYOUT } from '@backrs_ui/grow-ui/src/pages/CoachingAnalytics/analytics.layouts';
import { AnalyticsCardGrid } from '@backrs_ui/grow-ui/src/pages/CoachingAnalytics/AnalyticsCardGrid';
import { DEFAULT_FILTERS } from '@backrs_ui/grow-ui/src/pages/CoachingAnalytics/AnalyticsFilterBar';
import { SectionState } from '@backrs_ui/grow-ui/src/pages/CoachingAnalytics/AnalyticsSectionState';
import {
  engagementCards,
  VISIBLE_MONTHS,
} from '@backrs_ui/grow-ui/src/pages/CoachingAnalytics/EngagementCards';
import { useTopCardsAgentContext } from '@backrs_ui/grow-ui/src/pages/CoachingAnalytics/EngagementCards.agentContext';
import { overviewCards } from '@backrs_ui/grow-ui/src/pages/CoachingAnalytics/OverviewCards';
import { useAgentContext as useOverviewAgentContext } from '@backrs_ui/grow-ui/src/pages/CoachingAnalytics/OverviewCards.agentContext';
import {
  type EngagementResponse,
  type OverviewResponse,
  useGetCoachingAnalyticsEngagementQuery,
  useGetCoachingAnalyticsOverviewQuery,
} from '@backrs_ui/grow-ui/src/redux/coachingAnalytics';

import { Box, type SxProps, type Theme } from '@mui/material';

import { useAgentContext } from './HomeAnalyticsContent.agentContext';

type AnalyticsData = Readonly<{ engagement: EngagementResponse; overview: OverviewResponse }>;

export const HomeAnalyticsContent = () => {
  const navigate = useNavigate();
  const engagementQuery = useGetCoachingAnalyticsEngagementQuery({ filter: DEFAULT_FILTERS });
  const overviewQuery = useGetCoachingAnalyticsOverviewQuery({ filter: DEFAULT_FILTERS });

  const goToAnalyticsSection = (section: AnalyticsSection) =>
    navigate(`${APP_ROUTE.COACHING_ANALYTICS}#${ANALYTICS_SECTION_ID[section]}`);

  // A card can sit in either grid, so both responses have to be in before either renders.
  const query = {
    data:
      engagementQuery.data && overviewQuery.data
        ? ({ engagement: engagementQuery.data, overview: overviewQuery.data } as AnalyticsData)
        : undefined,
    isError: engagementQuery.isError || overviewQuery.isError,
  };

  return (
    <Box sx={containerSx}>
      <SectionState query={query}>
        {({ engagement, overview }) => (
          <HomeAnalyticsCards
            engagement={engagement}
            overview={overview}
            onSectionClick={goToAnalyticsSection}
          />
        )}
      </SectionState>
    </Box>
  );
};

const HomeAnalyticsCards = ({
  engagement,
  overview,
  onSectionClick,
}: Readonly<{
  engagement: EngagementResponse;
  overview: OverviewResponse;
  onSectionClick: (section: AnalyticsSection) => void;
}>) => {
  // Coach counts come back only for an institution's population, so their absence is
  // what says these numbers are one coach's -- not the viewer's permissions, which can
  // say admin while the response is a coach's.
  const coversAnInstitution = Boolean(overview.coaches);

  useTopCardsAgentContext(engagement, VISIBLE_MONTHS);
  useOverviewAgentContext(overview);
  useAgentContext(coversAnInstitution);

  const layout = coversAnInstitution ? HOME_LAYOUT.WITH_COACHES : HOME_LAYOUT.WITHOUT_COACHES;
  const cards = {
    ...engagementCards(engagement, () => onSectionClick('engagement')),
    ...overviewCards(overview, onSectionClick),
  };

  return (
    <>
      <AnalyticsCardGrid layout={layout.top} cards={cards} />
      <AnalyticsCardGrid layout={layout.overview} cards={cards} />
    </>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  // The cards size off this column, not the window: the Lubav panel takes width out of it.
  containerType: 'inline-size',
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
  pb: { xs: backrs.spacing['spacing-3xl'], md: backrs.spacing['spacing-xl'] },
});
